import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isEmptyObject } from '@utils/index';

type LikeObject = { count: number; isLiked?: boolean };

type LikesRecord = Record<string, LikeObject>;

type Likes = {
  byShow: Record<
    string,
    {
      byUser?: LikesRecord;
      bySpeaker?: LikesRecord;
    }
  >;
};

export const initialState: Likes = {
  byShow: {},
};

export const likesSlice = createSlice({
  name: 'likes',
  initialState,
  reducers: {
    setLikesByUser: (
      state,
      action: PayloadAction<{
        showId: string;
        likesByUser: Record<string, LikeObject>;
      }>,
    ) => {
      const { showId, likesByUser } = action.payload;

      if (!showId || isEmptyObject(likesByUser)) {
        return;
      }

      if (!state.byShow) {
        state.byShow = {};
      }

      const show = state.byShow?.[showId] || {};
      const byUser = show?.byUser || {};
      const newByUser: LikesRecord = {};

      Object.entries(likesByUser).forEach(([userId, likeObject]) => {
        const previousData = byUser?.[userId] || {};

        newByUser[userId] = {
          ...previousData,
          ...likeObject,
        };
      });

      state.byShow[showId] = {
        ...show,
        byUser: {
          ...byUser,
          ...newByUser,
        },
      };
    },
    setLikeByUser: (
      state,
      action: PayloadAction<{
        showId: string;
        userId: string;
        data: LikeObject;
      }>,
    ) => {
      const { showId, userId, data } = action.payload;

      if (!showId || isEmptyObject(data)) {
        return;
      }

      if (!state.byShow) {
        state.byShow = {};
      }

      const show = state.byShow?.[showId] || {};
      const byUser = show?.byUser || {};
      const userLikes = byUser?.[userId] || {};

      state.byShow[showId] = {
        ...show,
        byUser: {
          ...byUser,
          [userId]: {
            ...userLikes,
            ...data,
          },
        },
      };
    },
    setLikesBySpeaker: (
      state,
      action: PayloadAction<{
        showId: string;
        likesBySpeaker: Record<string, LikeObject>;
      }>,
    ) => {
      const { showId, likesBySpeaker } = action.payload;

      if (!showId || isEmptyObject(likesBySpeaker)) {
        return;
      }

      if (!state.byShow) {
        state.byShow = {};
      }

      const show = state.byShow?.[showId] || {};
      const bySpeaker = show?.bySpeaker || {};
      const newBySpeaker: LikesRecord = {};

      Object.entries(likesBySpeaker).forEach(([speakerId, likeObject]) => {
        const previousData = bySpeaker?.[speakerId] || {};

        newBySpeaker[speakerId] = {
          ...previousData,
          ...likeObject,
        };
      });

      state.byShow[showId] = {
        ...show,
        bySpeaker: {
          ...bySpeaker,
          ...newBySpeaker,
        },
      };
    },
    setLikeBySpeaker: (
      state,
      action: PayloadAction<{
        showId: string;
        speakerId: string;
        data: LikeObject;
      }>,
    ) => {
      const { showId, speakerId, data } = action.payload;

      if (!showId || isEmptyObject(data)) {
        return;
      }

      if (!state.byShow) {
        state.byShow = {};
      }

      const show = state.byShow?.[showId] || {};
      const bySpeaker = show?.bySpeaker || {};
      const userLikes = bySpeaker?.[speakerId] || {};

      state.byShow[showId] = {
        ...show,
        bySpeaker: {
          ...bySpeaker,
          [speakerId]: {
            ...userLikes,
            ...data,
          },
        },
      };
    },
  },
});

export const {
  setLikesByUser,
  setLikeByUser,
  setLikesBySpeaker,
  setLikeBySpeaker,
} = likesSlice.actions;

export default likesSlice.reducer;
